import Modal from "components/EvtKit/modal/Modal"

import { useModal } from "hooks/useModal"

import { memo, useCallback, useEffect, useState } from "react"
import styled from "styled-components"
import { AppState, useAppDispatch, useAppSelector } from "reducers/store"
import Button from "components/EvtKit/button/Button"
import { useMutation, useQueryClient } from "react-query"
import clsx from "clsx"
import Heading from "components/Typography/Heading"
import BodyText from "components/Typography/BodyText"
import { clearInvoice } from "reducers/invoice/invoice-actions"
import ApiInvoices from "api/ApiInvoices"
import { INVOICE_FETCH } from "reducers/invoice/invoice-types"
import { statusLabels } from "constants/invoice-table"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import useHandleApiError from "hooks/useHandleApiErrors"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const InfoItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const Divider = styled.div`
  border: 1px solid ${(props) => props.theme.palette.disabled};
  width: 100%;
`
const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`

const UpdateInvoiceModal = () => {
  const queryClient = useQueryClient()

  const appDispatch = useAppDispatch()

  const { isOpen, type, closeModal } = useModal()

  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState<string | null>(null)

  const { invoice } = useAppSelector((x: AppState) => x.invoices)

  const handleSelectedInvoiceStatusChange = useCallback((option?: Option | null) => {
    const optionValue = option?.value

    if (optionValue) {
      setSelectedInvoiceStatus(optionValue)
    }
  }, [])

  const handleCleanModalState = useCallback(() => {
    appDispatch(clearInvoice())
    closeModal()
  }, [])

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const updateInvoice = useMutation({
    mutationFn: async (data: App.InvoiceUpdateStatusPayload) => {
      return new ApiInvoices().updateInvoice(data)
    },
    onSuccess: () => {
      handleCleanModalState()
      queryClient.invalidateQueries(INVOICE_FETCH)
    },
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, "Erro ao atualizar a invoice.")
    }
  })

  const handleUpdateStatus = useCallback(async () => {
    if (!invoice?.id || !selectedInvoiceStatus) return

    const invoiceUpdatePayload: App.InvoiceUpdateStatusPayload = {
      id: invoice.id,
      status: selectedInvoiceStatus
    }

    updateInvoice.mutate(invoiceUpdatePayload)
  }, [invoice, selectedInvoiceStatus])

  useEffect(() => {
    setSelectedInvoiceStatus(invoice?.status || null)
  }, [invoice])

  return (
    <Modal openModal={isOpen && type === "update"} closeModal={handleCleanModalState} size="sm">
      <Container>
        <Heading variant="h4" color="gray-dark">
          Atualizar Fatura
        </Heading>
        <Divider />
        <InfoContainer>
          <InfoItemContainer>
            <BodyText weight="light" size="medium">
              Produto
            </BodyText>
            <BodyText weight="bold" size="medium">
              {invoice?.productName}
            </BodyText>
          </InfoItemContainer>
          <InfoItemContainer>
            <BodyText weight="light" size="medium">
              Cnpj
            </BodyText>
            <BodyText weight="bold" size="medium">
              {invoice?.cnpj}
            </BodyText>
          </InfoItemContainer>
          <InfoItemContainer>
            <BodyText weight="light" size="medium">
              ID
            </BodyText>
            <BodyText weight="bold" size="medium">
              {invoice?.id}
            </BodyText>
          </InfoItemContainer>
        </InfoContainer>
        <Divider />
        <SelectV2
          id="selectedItemStatus"
          value={selectedInvoiceStatus || ""}
          onChange={handleSelectedInvoiceStatusChange}
          options={statusLabels}
          label="Status"
        />
        <ButtonsContainer>
          <Button
            type="button"
            onClick={handleCleanModalState}
            label="Cancelar"
            className={clsx("secondary fill-width")}
          />
          <Button
            loading={updateInvoice.isLoading}
            type="submit"
            onClick={handleUpdateStatus}
            label="Salvar"
            className={clsx("background-gradient fill-width")}
            disabled={invoice.status === selectedInvoiceStatus || updateInvoice.isLoading}
          />
        </ButtonsContainer>
      </Container>
    </Modal>
  )
}

export default memo(UpdateInvoiceModal)
